import { Injectable } from '@angular/core';
import { ElementDTO, Element } from 'src/app/core/interfaces/element';
import { Observable, forkJoin, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TextWyswygComponent } from 'src/app/modules/elements/text-wyswyg/text-wyswyg.component';
import { ImageWyswygComponent } from 'src/app/modules/elements/image-wyswyg/image-wyswyg.component';
import { VideoWyswygComponent } from 'src/app/modules/elements/video-wyswyg/video-wyswyg.component';
import { QuizWyswygComponent } from 'src/app/modules/elements/quiz-wyswyg/quiz-wyswyg.component';
import { HrWyswygComponent } from 'src/app/modules/elements/hr-wyswyg/hr-wyswyg.component';
import { ListWyswygComponent } from 'src/app/modules/elements/list-wyswyg/list-wyswyg.component';
import { UrlWyswygComponent } from 'src/app/modules/elements/url-wyswyg/url-wyswyg.component';
import { CodeWyswygComponent } from 'src/app/modules/elements/code-wyswyg/code-wyswyg.component';
import { TextRenderedComponent } from 'src/app/modules/elements/text-rendered/text-rendered.component';
import { ImageRenderedComponent } from 'src/app/modules/elements/image-rendered/image-rendered.component';
import { VideoRenderedComponent } from 'src/app/modules/elements/video-rendered/video-rendered.component';
import { QuizRenderedComponent } from 'src/app/modules/elements/quiz-rendered/quiz-rendered.component';
import { HrRenderedComponent } from 'src/app/modules/elements/hr-rendered/hr-rendered.component';
import { ListRenderedComponent } from 'src/app/modules/elements/list-rendered/list-rendered.component';
import { CodeRenderedComponent } from 'src/app/modules/elements/code-rendered/code-rendered.component';
import { UrlRenderedComponent } from 'src/app/modules/elements/url-rendered/url-rendered.component';
import { FileWyswygComponent } from 'src/app/modules/elements/file-wyswyg/file-wyswyg.component';
import { FileRenderedComponent } from 'src/app/modules/elements/file-rendered/file-rendered.component';
import { environment } from 'src/environments/environments';
import { UsersService } from './users.service';

@Injectable({
  providedIn: 'root'
})
export class ElementsService {
  private elementsBaseUrl = environment.elementsBaseUrl;


  constructor(private http: HttpClient,
    private userService: UsersService
  ) { }

  // CRUD methods
  getAll(): Observable<Element[]> {
    return this.http.get<any>(this.elementsBaseUrl).pipe(
      map(elements => {
        return elements.map(element => this.parseElement(element));
      }),
    )
  }

  getByTabId(tabId: string): Observable<Element[]> {
    return this.http
      .get<any>(`${this.elementsBaseUrl}ByTab/${tabId}`).pipe(
        map(elements => {
          elements = elements.map(element => this.parseElement(element))
          elements.sort((a, b) => a.elementOrder - b.elementOrder);
          return elements;
        }),
      );
  }

  getAnswers(id: string): Observable<any> {
    return this.http.get<any>(`${this.elementsBaseUrl}/${id}`).pipe(
      map(element => this.parseElement(element).data.answers.filter(a => a.correct)),
    )
  }

  checkAnswers(quizId: string, session: string, answers: any[]): Observable<any> {
    return this.http.post(`https://${environment.baseUrl}/php/checkAnswer.php`, {quizId, session, answers})
  }

  saveAll(elements: Element[], lessonId): Observable<any> {

    const elementsDto = elements.map(element => this.parseDTO(element));
    const organizationId = this.userService.getUserData()?.organizationId;
    return this.http.post(`${this.elementsBaseUrl}/batch/${lessonId}`, {elements: elementsDto, organizationId});
  }

  deleteAll(ids: string[], lessonId): Observable<any> {
    const organizationId = this.userService.getUserData()?.organizationId;
    return this.http.delete(`${this.elementsBaseUrl}/batch/${lessonId}`,{body: {ids, organizationId}});
  }


  /* From view to database */
  private parseDTO(component: Element): ElementDTO {
    let newDTO: ElementDTO = {
      id: component.id,
      typeElement: "",
      elementOrder: component.elementOrder,
      tabId: component.tabId,
      pageNumber: component.pageNumber,
      stylecss: component.stylecss ?? {}
    };

    switch (Object.getPrototypeOf(component).constructor) {
      case TextRenderedComponent:
        newDTO.typeElement = "text";
        newDTO.contentText = { text: component.data.text }
        break;
      case ImageRenderedComponent:
        newDTO.typeElement = "img";
        newDTO.contentImg = {
          src: component.data.src,
          alt: component.data.alt
        }
        newDTO.uploadData = component.data.uploadData;
        break;
      case FileRenderedComponent:
        newDTO.typeElement = "file";
        newDTO.contentFile = {
          src: component.data.src,
        }
        newDTO.uploadData = component.data.uploadData;
        break;
      case VideoRenderedComponent:
        newDTO.typeElement = "vid";
        newDTO.contentVid = { src: component.data.src }
        break;
      case QuizRenderedComponent:
        newDTO.typeElement = "quiz";
        newDTO.contentQuiz = {
          question: component.data.question,
          test: component.data.test,
          answers: component.data.answers
        }
        break;
      case HrRenderedComponent:
        newDTO.typeElement = "hr";
        newDTO.contentHr = {}
        break;
      case ListRenderedComponent:
        newDTO.typeElement = "list";
        newDTO.contentList = {
          list: component.data.list,
          ordered: component.data.ordered,
          mark: component.data.mark,
          indent: component.data.indent ?? 0,
          startList: component.data.startList ?? 1,
        }
        break;
      case UrlRenderedComponent:
        newDTO.typeElement = "url";
        newDTO.contentUrl = {
          text: component.data.text,
          href: component.data.href
        }
        break;
      case CodeRenderedComponent:
        newDTO.typeElement = "code";
        newDTO.contentCode = {
          text: component.data.text,
          codeLang: component.data.codeLang
        }
        break;
      default:
        throw new Error('Wrong type element');
    }
    return newDTO;
  }

  /* From database to view */
  parseElement(element: ElementDTO): Element {
    let newComponent: Element;
    switch (element.typeElement) {
      case "text":
        newComponent = new TextRenderedComponent();
        newComponent.data = element.contentText;
        break;
      case "img":
        newComponent = new ImageRenderedComponent();
        newComponent.data = element.contentImg;
        delete newComponent.data.uploadData;
        break;
      case "file":
        newComponent = new FileRenderedComponent();
        newComponent.data = element.contentFile;
        delete newComponent.data.uploadData;
        break;
      case "vid":
        newComponent = new VideoRenderedComponent();
        newComponent.data = element.contentVid;
        break;
      case "quiz":
        newComponent = new QuizRenderedComponent();
        newComponent.data = element.contentQuiz;
        newComponent.data.txtAnswer = '';
        newComponent.data.answers.map (answer => {
          answer.checked = false
          return answer;
        })
        break;
      case "hr":
        newComponent = new HrRenderedComponent();
        newComponent.data = element.contentHr;
        break;
      case "list":
        newComponent = new ListRenderedComponent();
        newComponent.data = element.contentList;
        break;
      case "url":
        newComponent = new UrlRenderedComponent();
        newComponent.data = element.contentUrl;
        break;
      case "code":
        newComponent = new CodeRenderedComponent();
        newComponent.data = element.contentCode;
        break;
      default:
        throw new Error('Wrong type element');
    }
    newComponent.id = element.id;
    newComponent.elementOrder = element.elementOrder;
    newComponent.pageNumber = element.pageNumber;
    newComponent.stylecss = element.stylecss;
    newComponent.tabId = element.tabId;

    this.setOriginalData(newComponent, element.typeElement);

    return newComponent;
  }

  setOriginalData(element: Element, typeElement?: string) {
    element.originalData = { ...element.data }

    typeElement ??= this.getTypeElement(Object.getPrototypeOf(element).constructor);
    // unlink sublists and subobjects
    switch (typeElement) {
      case "list":
        element.originalData.list = [...element.data.list];
        break;
      case "quiz":
        element.originalData.answers = [...element.data.answers];
        element.data.answers.forEach((answer, i) => {
          element.originalData.answers[i] = {...answer};
        })
        break;
      default:
        break;
    }

    delete element.originalData.prefix;
    delete element.originalData.ytVideoId;
  }


  renderComponent(element: Element, prefix: string,
    viewContainerRef, render: boolean, index?: number): void {
    element.data = element.data || {};
    // prefix: para usarlo como id única del elemento html en la tab seleccionada
    element.data.prefix = prefix;

    const constructor = Object.getPrototypeOf(element).constructor;
    const componentRef =
        viewContainerRef.createComponent(render ? constructor : this.toEditor(constructor), { index });
    componentRef.instance.data = element.data;
    // componentRef.instance.originalData = { ...element.data };
    componentRef.instance.id = element.id; // id de base de datos
    componentRef.instance.rendered = render;
    componentRef.instance.elementOrder = element.elementOrder;
  }

  getTypeElement(constructor): string {
    switch (constructor) {
      case TextWyswygComponent:
      case TextRenderedComponent:
        return "text";
      case ImageWyswygComponent:
      case ImageRenderedComponent:
        return "img";
      case FileWyswygComponent:
      case FileRenderedComponent:
        return "file";
      case VideoWyswygComponent:
      case VideoRenderedComponent:
        return "video";
      case QuizWyswygComponent:
      case QuizRenderedComponent:
        return "quiz";
      case HrWyswygComponent:
      case HrRenderedComponent:
        return "hr";
      case ListWyswygComponent:
      case ListRenderedComponent:
        return "list";
      case UrlWyswygComponent:
      case UrlRenderedComponent:
          return "url";
      case CodeWyswygComponent:
      case CodeRenderedComponent:
        return "code";

      default:
        throw new Error('Wrong type element');
    }
  }

  toRendered(constructor) {
    switch (constructor) {
      case TextWyswygComponent:
        return TextRenderedComponent;
      case ImageWyswygComponent:
        return ImageRenderedComponent;
      case FileWyswygComponent:
        return FileRenderedComponent;
      case VideoWyswygComponent:
        return VideoRenderedComponent;
      case QuizWyswygComponent:
        return QuizRenderedComponent;
      case HrWyswygComponent:
        return HrRenderedComponent;
      case ListWyswygComponent:
        return ListRenderedComponent;
      case UrlWyswygComponent:
        return UrlRenderedComponent;
      case CodeWyswygComponent:
        return CodeRenderedComponent;

      default:
        throw new Error('Wrong type element');
    }
  }

  toEditor(constructor) {
    switch (constructor) {
      case TextRenderedComponent:
        return TextWyswygComponent;
      case ImageRenderedComponent:
        return ImageWyswygComponent;
      case FileRenderedComponent:
        return FileWyswygComponent;
      case VideoRenderedComponent:
        return VideoWyswygComponent;
      case QuizRenderedComponent:
        return QuizWyswygComponent;
      case HrRenderedComponent:
        return HrWyswygComponent;
      case ListRenderedComponent:
        return ListWyswygComponent;
      case UrlRenderedComponent:
        return UrlWyswygComponent;
      case CodeRenderedComponent:
        return CodeWyswygComponent;

      default:
        throw new Error('Wrong type element');
    }
  }

}
